import {Button, Card, Form, Input, InputNumber, Modal, Switch, Table, Tabs} from "antd";
import {useEffect, useRef, useState} from "react";
import {t} from "i18next";
import {useHistory, useParams} from "react-router-dom";
import SimpleLayout from "../../core/components/layout/SimpleLayout";
import ApiService from "../../core/service/ApiService";
import FirmSelectList from "../firm/FirmSelectList";
import FileUtils from "../../core/utils/file-utils";
import Helper from "../../core/Helper";

export default function UnitCreateEdit({})
{
    const {unitId} = useParams();
    const [unit, setUnit] = useState({});
    const [visible, setVisible] = useState(false);
    const [form] = Form.useForm();
    const file = useRef(null);

    const history = useHistory();
    const formRef = useRef();

    const columns = [
        {
            title: t('ID'),
            dataIndex: 'id',
            key: 'id'
        },
        {
            title: t('Language'),
            dataIndex: 'language',
            key: 'language'
        },
        {
            title: '#',
            key: 'actions',
            render: (_, record) => (
                <>
                    <Button type="link" onClick={() => handleDownload(record.id)}>{t('Download')}</Button>
                    <Button type="link" danger onClick={() => handleDelete(record.id)}>{t('Delete')}</Button>
                </>
            )
        }
    ];

    const handleDelete = async (id) => {
        const res = await ApiService.Unit.DeleteUnitForm(unitId, id);
        if(res.success) {
            await getUnit()
        }
    };

    const handleDownload = async (id) => {
        const res = await ApiService.Unit.GetDefaultForm(unitId, id);
        if(res.success) {
            Helper.File.saveFile({
                blobLink: res.data,
                fileName: res.fileName
            })
        }
    };

    const showModal = () => {
        setVisible(true);
    };

    const handleOk = async () => {
        await form.validateFields();
        const values = form.getFieldsValue();

        const res = await ApiService.Unit.UploadDefaultForm(unitId, {
            language: values.language,
            fileBase64: file.current.data,
        })

        if(res.success) {
            form.resetFields();
            setVisible(false);
            await getUnit()
        }
    };

    const handleCancel = () => {
        setVisible(false);
    };

    const onFinish = async (formData) => {
        if(unitId)
        {
            const res = await ApiService.Unit.EditUnit(unitId, {
                ...formData
            })
        }
        else
        {
            const res = await ApiService.Unit.CreateUnit({
                ...formData
            })

            if(res.success)
            {
                history.push(`/app/system/units/edit/${res.data}`)
            }
        }
    }

    const getUnit = async () => {
        if(unitId)
        {
            const res = await ApiService.Unit.GetUnit(unitId);
            if(res.success)
            {
                setUnit(res.data)
                formRef.current.setFieldsValue({
                    name: res.data.name,
                    visibleName: res.data.visibleName,
                    firmId: res.data.firmId,
                    hbysForeignKey: res.data.hbysForeignKey,
                    hbysFirmName: res.data.hbysFirmName,
                    hbysInstitutionName: res.data.hbysInstitutionName,
                    hbysInstitutionCode: res.data.hbysInstitutionCode,
                    hbysDoctor: res.data.hbysDoctor,
                    hl7Ip: res.data.hl7Ip,
                    hl7Port: res.data.hl7Port,
                    hl7TlsEnabled: res.data.hl7TlsEnabled,
                    hospitalTeletipId: res.data.hospitalTeletipId,
                    enabizEnabled: res.data.enabizEnabled,
                    enableNotification: res.data.enableNotification,
                    teletipUserName: res.data.teletipUserName,
                    teletipPassword: res.data.teletipPassword,
                    teletipApplicationCode: res.data.teletipApplicationCode,
                    medulaInstitutionId: res.data.medulaInstitutionId
                })
            }
        }
    }

    useEffect(() => {
        getUnit()
    }, [unitId])

    return (
        <SimpleLayout>
            <Card
                title={!unitId ? t('New Unit') : `${t('Edit Unit')} - ${unit.name}`}
            >
                <Tabs>
                    <Tabs.TabPane tab={t('Unit')} key="unit">
                        <Form
                            ref={formRef}
                            layout={'vertical'}
                            onFinish={onFinish}
                        >
                            <Form.Item
                                label={t('Firm')}
                                name={'firmId'}
                                required={true}
                            >
                                <FirmSelectList />
                            </Form.Item>

                            <Form.Item
                                label={t('Name')}
                                name={'name'}
                                required={true}
                            >
                                <Input />
                            </Form.Item>

                            <Form.Item
                                label={t('Visible Name')}
                                name={'visibleName'}
                                tooltip={t('Shown in messages via the {UNIT} placeholder. If empty, Name is used.')}
                            >
                                <Input placeholder={t('If empty, Name is used.')} />
                            </Form.Item>

                            <Card title={t('HBYS Teletip Integration')} size="small" style={{ marginBottom: 16 }}>
                                <Form.Item
                                    label={t('HBYS Foreign Key')}
                                    name={'hbysForeignKey'}
                                >
                                    <Input placeholder="örn: UNIT001" />
                                </Form.Item>

                                <Form.Item
                                    label={t('HBYS Firm Name')}
                                    name={'hbysFirmName'}
                                >
                                    <Input placeholder="örn: HASTANE ADI" />
                                </Form.Item>

                                <Form.Item
                                    label={t('HBYS Institution Name')}
                                    name={'hbysInstitutionName'}
                                >
                                    <Input placeholder="örn: RADYOLOJİ BÖLÜMÜ" />
                                </Form.Item>

                                <Form.Item
                                    label={t('HBYS Institution Code')}
                                    name={'hbysInstitutionCode'}
                                >
                                    <Input placeholder="örn: 123456" />
                                </Form.Item>

                                <Form.Item
                                    label={t('HBYS Doctor')}
                                    name={'hbysDoctor'}
                                >
                                    <Input placeholder="örn: Dr. Adı Soyadı" />
                                </Form.Item>

                                <Form.Item
                                    label={t('HL7 IP Address')}
                                    name={'hl7Ip'}
                                >
                                    <Input placeholder="örn: 192.168.1.100" />
                                </Form.Item>

                                <Form.Item
                                    label={t('HL7 Port')}
                                    name={'hl7Port'}
                                >
                                    <InputNumber min={1} max={65535} placeholder="örn: 2575" style={{ width: '100%' }} />
                                </Form.Item>

                                <Form.Item
                                    label={t('HL7 TLS Enabled')}
                                    name={'hl7TlsEnabled'}
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>

                                <Form.Item
                                    label={t('Hospital Teletip ID')}
                                    name={'hospitalTeletipId'}
                                >
                                    <InputNumber min={1} placeholder="örn: 1" style={{ width: '100%' }} />
                                </Form.Item>

                                <Form.Item
                                    label={t('E-Nabız Gönderim Durumu')}
                                    name={'enabizEnabled'}
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>

                                <Form.Item
                                    label={t('Send Notifications')}
                                    name={'enableNotification'}
                                    valuePropName="checked"
                                    initialValue={true}
                                    tooltip={t('When off, no notifications are produced/sent for appointments in this unit.')}
                                >
                                    <Switch />
                                </Form.Item>

                                <Form.Item
                                    label={t('Teletip Kullanıcı Adı')}
                                    name={'teletipUserName'}
                                >
                                    <Input placeholder="örn: kullanici@saglik.gov.tr" />
                                </Form.Item>

                                <Form.Item
                                    label={t('Teletip Şifre')}
                                    name={'teletipPassword'}
                                >
                                    <Input.Password placeholder="Şifre" />
                                </Form.Item>

                                <Form.Item
                                    label={t('Teletip Application Code')}
                                    name={'teletipApplicationCode'}
                                >
                                    <Input placeholder="örn: APP001" />
                                </Form.Item>

                                <Form.Item
                                    label={t('Medula Institution ID')}
                                    name={'medulaInstitutionId'}
                                >
                                    <InputNumber min={1} placeholder="örn: 123456" style={{ width: '100%' }} />
                                </Form.Item>
                            </Card>

                            <Form.Item>
                                <Button htmlType={'submit'} type={'primary'}>{t('Save')}</Button>
                            </Form.Item>
                        </Form>
                    </Tabs.TabPane>
                    <Tabs.TabPane tab={t('Unit Forms')} key="unitForms">
                        <div>
                            <Button
                                type="primary"
                                onClick={showModal}
                                size={'small'}
                            >
                                Yeni Kayıt Ekle
                            </Button>
                            <Table
                                dataSource={unit.unitForms}
                                columns={columns}
                                rowKey="id"
                                size={'small'}
                                pagination={false}
                            />

                            <Modal
                                title="Yeni Kayıt Ekle"
                                visible={visible}
                                onOk={handleOk}
                                onCancel={handleCancel}
                                okText="Kaydet"
                                cancelText="Vazgeç"
                            >
                                <Form form={form} layout="vertical">
                                    <Form.Item name="language" label={t('Language')} rules={[{ required: true, message: t('Please input a language!') }]}>
                                        <Input />
                                    </Form.Item>
                                    <Form.Item name="fileName" label={t('File')} rules={[{ required: true, message: t('Please select a file!') }]}>
                                        <Input disabled={true}/>
                                    </Form.Item>
                                    <Button
                                        size={'small'}
                                        onClick={async () => {
                                            file.current = await FileUtils.getFileAsBase64({})
                                            form.setFieldsValue({
                                                fileName: file.current.fileName,
                                            })
                                        }}
                                    >
                                        {t('Select File')}
                                    </Button>
                                </Form>
                            </Modal>
                        </div>
                    </Tabs.TabPane>
                </Tabs>
            </Card>
        </SimpleLayout>
    )
}